import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Env_Factor_Options } from "../../db_access/env_factor_options";
import "../../style/components-style/MaterialSearchCellEditor.css";

const MaterialSearchCellEditor = forwardRef((props, ref) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const selectedMaterialRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, []);

    useEffect(() => {
        const search = async () => {
            if (searchTerm.length < 2) {
                setSearchResults([]);
                return;
            }

            setIsLoading(true);
            try {
                const response = await Env_Factor_Options.searchMaterials(searchTerm);
                const data = await response.json();
                setSearchResults(data || []);
                setSelectedIndex(0);
            } catch (error) {
                console.error("Error searching materials:", error);
                setSearchResults([]);
            } finally {
                setIsLoading(false);
            }
        };

        const debounceTimer = setTimeout(search, 300);
        return () => clearTimeout(debounceTimer);
    }, [searchTerm]);

    useEffect(() => {
        if (listRef.current && searchResults.length > 0) {
            const selectedElement = listRef.current.children[selectedIndex];
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: "nearest" });
            }
        }
    }, [selectedIndex, searchResults]);

    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex((prev) => Math.min(prev + 1, searchResults.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === "Enter" || e.key === "Tab") {
            if (searchResults.length > 0) {
                e.preventDefault();
                selectMaterial(searchResults[selectedIndex]);
            }
        } else if (e.key === "Escape") {
            if (props.stopEditing) {
                props.stopEditing(true);
            }
        }
    };

    const selectMaterial = (material) => {
        // Store the material data for onCellValueChanged
        if (props.data) {
            props.data._selectedMaterialFromSearch = material;
        }

        // Directly update the cell value using the grid API
        if (props.node && props.column) {
            props.node.setDataValue(props.column.colId, material.description);
        }
    };

    useImperativeHandle(ref, () => ({
        getValue: () => {
            const value = selectedMaterialRef.current ? selectedMaterialRef.current.description : searchTerm;
            return value;
        },
        isCancelAfterEnd: () => {
            return false;
        },
    }));

    return (
        <div className="material-search-editor">
            <input
                ref={inputRef}
                type="text"
                className="material-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type to search materials..."
            />
            {isLoading && <div className="material-search-loading">Searching...</div>}
            {searchResults.length > 0 && (
                <div className="material-search-results" ref={listRef}>
                    {searchResults.map((material, index) => {
                        const parts = [];
                        if (material.length !== null && material.length !== undefined && material.length !== "") parts.push(`${material.length}${material.length_unit || ""}`);
                        if (material.width !== null && material.width !== undefined && material.width !== "") parts.push(`${material.width}${material.width_unit || ""}`);
                        if (material.thickness !== null && material.thickness !== undefined && material.thickness !== "") parts.push(`${material.thickness}${material.thickness_unit || ""}`);
                        const dimString = parts.length > 0 ? parts.join(" × ") : null;

                        return (
                            <div
                                key={index}
                                className={`material-search-item ${index === selectedIndex ? "selected" : ""}`}
                                onClick={() => selectMaterial(material)}
                                onMouseEnter={() => setSelectedIndex(index)}
                            >
                                <div className="material-item-left">
                                    <div className="material-description">{material.description}</div>
                                    <div className="material-details">
                                        <span className="material-type">{material.type}</span>
                                        {" → "}
                                        <span className="material-category">{material.category}</span>
                                        {" → "}
                                        <span className="material-subcategory">{material.sub_category}</span>
                                    </div>
                                </div>
                                <div className="material-item-right">
                                    {dimString && (
                                        <span className="material-dim-pill">
                                            📏 {dimString}
                                        </span>
                                    )}
                                    {material.unit && (
                                        <span className="material-unit-pill">
                                            Unit: {material.unit}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {!isLoading && searchTerm.length >= 2 && searchResults.length === 0 && (
                <div className="material-search-no-results">No materials found</div>
            )}
        </div>
    );
});

export default MaterialSearchCellEditor;
